import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { CATEGORIES } from "@/config/site";
import { LogoMark } from "@/components/site/Logo";
import { ProductDialog } from "@/components/admin/ProductDialog";
import { ProductRow } from "@/components/admin/ProductRow";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-[#16161A] p-5">
    <p className="eyebrow">{label}</p>
    <p data-testid={testid} className="mt-3 font-display text-4xl">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);
  const [dialog, setDialog] = useState({ open: false, product: null });
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await http.get("/products", { params: { all: true } });
    setItems(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    http.get("/auth/me").then(() => load()).then(() => setReady(true)).catch(() => {
      localStorage.removeItem("mp_token");
      nav("/admin/login");
    });
  }, [load, nav]);

  const logout = () => {
    localStorage.removeItem("mp_token");
    nav("/admin/login");
  };

  const save = async (body) => {
    setSaving(true);
    try {
      const { product } = dialog;
      if (product) await http.put(`/products/${product.id}`, body);
      else await http.post("/products", body);
      toast.success(product ? "Prodotto aggiornato" : "Prodotto aggiunto");
      setDialog({ open: false, product: null });
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p, available) => {
    try {
      const { id, created_at, ...rest } = p;
      await http.put(`/products/${id}`, { ...rest, available });
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await http.delete(`/products/${toDelete.id}`);
      toast.success("Prodotto eliminato");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!ready) return <div className="min-h-screen grid place-items-center text-zinc-400">Caricamento…</div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0D0D0F]/80 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3"><LogoMark className="w-9 h-9" /><span className="font-display uppercase text-lg">Pannello menù</span></div>
          <div className="flex items-center gap-2">
            <Link to="/menu" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hover:bg-white/10 transition-colors"><ExternalLink className="w-4 h-4" /> Vedi menù</Link>
            <button type="button" onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:bg-white/10 transition-colors"><LogOut className="w-4 h-4" /> Esci</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-10">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Prodotti" value={items.length} testid="stat-total" />
          <Stat label="Disponibili" value={items.filter((p) => p.available).length} testid="stat-available" />
          <Stat label="Categorie" value={new Set(items.map((p) => p.category)).size} testid="stat-categories" />
        </div>
        <div className="mt-10 flex items-center justify-between gap-4">
          <h1 className="font-display uppercase text-3xl sm:text-4xl tracking-[-0.03em]">Prodotti</h1>
          <button type="button" onClick={() => setDialog({ open: true, product: null })} data-testid="admin-add-product" className="btn-primary !py-3"><Plus className="w-4 h-4" /> Nuovo</button>
        </div>
        {CATEGORIES.map((c) => {
          const list = items.filter((p) => p.category === c);
          if (!list.length) return null;
          return (
            <section key={c} className="mt-8">
              <p className="eyebrow mb-3">{c}</p>
              <ul className="grid gap-2">
                {list.map((p) => <ProductRow key={p.id} p={p} onEdit={(x) => setDialog({ open: true, product: x })} onDelete={setToDelete} onToggle={toggle} />)}
              </ul>
            </section>
          );
        })}
      </main>
      <ProductDialog open={dialog.open} product={dialog.product} saving={saving} onSave={save} onOpenChange={(o) => setDialog((s) => ({ ...s, open: o }))} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#16161A] border-white/10 rounded-[1.5rem]">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Il prodotto sparirà dal menù. L'azione non si può annullare.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel" className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={remove} className="rounded-full bg-red-500 text-white hover:bg-red-600">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
